/* TPL PDP Reviews - media upload for the write-a-review sheet.

   Rides Judge.me's own upload path, the one its widget uses:
     1. GET  api.judge.me/api/v1/{pictures|videos}/presigned_data
     2. POST the file to the returned S3 form (key = key_prefix + name,
        every presigned field, then `file` last)
     3. S3 answers 201 with <Key>; tpl-pdp-reviews.js sends those keys with
        the review as picture_keys[i] / video_keys[i].

   Limits mirror Judge.me's widget: 5 photos at 10 MB, 1 video at 100 MB.
   Images are re-encoded to JPEG and capped at 2048px on the long edge
   before upload: a 10 MB phone photo becomes ~1 MB on a mobile
   connection, and Judge.me serves review images at 1024px anyway.

   Exposes window.tplReviewMedia = { init(form, fail), uploadAll(entries,
   shopDomain, onStatus) }. Loaded with defer before tpl-pdp-reviews.js. */
(function () {
  'use strict';

  var MEDIA_RULES = {
    image: { max: 5, maxMB: 10, resource: 'pictures', keyField: 'picture_keys', noun: 'photo' },
    video: { max: 1, maxMB: 100, resource: 'videos', keyField: 'video_keys', noun: 'video' }
  };
  var IMAGE_MAX_EDGE = 2048;
  var IMAGE_JPEG_QUALITY = 0.85;
  var PRESIGN_BASE = 'https://api.judge.me/api/v1/';
  var VIDEO_EXT_RE = /\.(mp4|mov|m4v|webm|3gp|mkv|avi)$/i;
  var IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|heic|heif)$/i;

  function mediaKind(file) {
    var type = (file.type || '').toLowerCase();
    if (type.indexOf('image/') === 0) return 'image';
    if (type.indexOf('video/') === 0) return 'video';
    /* Some Android pickers hand over .mov/.heic with an empty type. */
    if (VIDEO_EXT_RE.test(file.name)) return 'video';
    if (IMAGE_EXT_RE.test(file.name)) return 'image';
    return null;
  }

  function countKind(entries, kind) {
    return entries.filter(function (e) {
      return e.kind === kind;
    }).length;
  }

  /* Returns { entries, error }: the new immutable list plus the first
     reason a file was refused (null when everything was accepted). */
  function acceptFiles(entries, fileList) {
    var next = entries.slice();
    var error = null;
    Array.prototype.forEach.call(fileList, function (file) {
      var kind = mediaKind(file);
      if (!kind) {
        error = error || 'Only photos and videos can be attached.';
        return;
      }
      var rule = MEDIA_RULES[kind];
      if (countKind(next, kind) >= rule.max) {
        error = error || 'Up to ' + rule.max + ' ' + rule.noun + (rule.max > 1 ? 's' : '') + ' per review.';
        return;
      }
      if (file.size / 1048576 > rule.maxMB) {
        error = error || file.name + ' is over ' + rule.maxMB + ' MB. Try a smaller ' + rule.noun + '.';
        return;
      }
      next = next.concat([{ file: file, kind: kind, url: URL.createObjectURL(file) }]);
    });
    return { entries: next, error: error };
  }

  function safeFileName(name, forcedExt) {
    var base = String(name || 'upload').replace(/[^a-z0-9\-_.]/gi, '');
    var dot = base.lastIndexOf('.');
    var stem = dot > 0 ? base.slice(0, dot) : base;
    var ext = dot > 0 ? base.slice(dot + 1) : '';
    if (forcedExt) ext = forcedExt;
    if (!stem) stem = 'upload';
    return Date.now() + '-' + stem + (ext ? '.' + ext : '');
  }

  /* Downscale + JPEG re-encode. Falls back to the original file when the
     browser cannot decode it (HEIC on Chrome) so nothing is silently
     dropped; Judge.me handles those server-side like its own widget. */
  function prepareImage(file) {
    if (file.type === 'image/gif' || !('createObjectURL' in URL)) return Promise.resolve(file);
    return new Promise(function (resolve) {
      var img = new Image();
      var url = URL.createObjectURL(file);
      img.onload = function () {
        URL.revokeObjectURL(url);
        var scale = Math.min(1, IMAGE_MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
        var canvas = document.createElement('canvas');
        canvas.width = Math.round(img.naturalWidth * scale);
        canvas.height = Math.round(img.naturalHeight * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(function (blob) {
          resolve(blob ? { blob: blob, name: safeFileName(file.name, 'jpg') } : file);
        }, 'image/jpeg', IMAGE_JPEG_QUALITY);
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        resolve(file);
      };
      img.src = url;
    });
  }

  function fetchPresigned(shopDomain, resource, cache) {
    if (!cache[resource]) {
      var url =
        PRESIGN_BASE + resource + '/presigned_data?url=' + encodeURIComponent(shopDomain) +
        '&shop_domain=' + encodeURIComponent(shopDomain) + '&platform=shopify';
      cache[resource] = fetch(url)
        .then(function (res) {
          if (!res.ok) throw new Error('presign ' + res.status);
          return res.json();
        })
        .then(function (data) {
          if (!data || typeof data.url !== 'string' || !data.fields) throw new Error('presign shape');
          return data;
        })
        .catch(function (err) {
          delete cache[resource];
          throw err;
        });
    }
    return cache[resource];
  }

  /* POST to the S3 form. XHR (not fetch) for upload progress. Resolves
     the object key S3 echoes back, which is what Judge.me expects. */
  function uploadToS3(presigned, blob, fileName, onProgress) {
    return new Promise(function (resolve, reject) {
      var body = new FormData();
      body.append('key', presigned.key_prefix + encodeURIComponent(fileName));
      Object.keys(presigned.fields).forEach(function (k) {
        body.append(k, presigned.fields[k]);
      });
      body.append('file', blob, fileName);
      var xhr = new XMLHttpRequest();
      xhr.open('POST', presigned.url);
      xhr.upload.addEventListener('progress', function (e) {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      });
      xhr.onload = function () {
        if (xhr.status < 200 || xhr.status >= 300) return reject(new Error('s3 ' + xhr.status));
        var doc = new DOMParser().parseFromString(xhr.responseText, 'text/xml');
        var keyNode = doc.querySelector('Key');
        var key = keyNode && keyNode.textContent;
        if (!key) return reject(new Error('s3 no key'));
        resolve(key);
      };
      xhr.onerror = function () {
        reject(new Error('s3 network'));
      };
      xhr.ontimeout = function () {
        reject(new Error('s3 timeout'));
      };
      xhr.send(body);
    });
  }

  /* Uploads every entry in turn. Resolves [{ keyField, key }] in the
     order picked; rejects with the failing entry attached. */
  function uploadAll(entries, shopDomain, onStatus) {
    var cache = {};
    var results = [];
    return entries
      .reduce(function (chain, entry, i) {
        return chain.then(function () {
          var rule = MEDIA_RULES[entry.kind];
          var label = rule.noun + ' ' + (i + 1) + ' of ' + entries.length;
          onStatus('Uploading ' + label + '…');
          var prep = entry.kind === 'image' ? prepareImage(entry.file) : Promise.resolve(entry.file);
          return Promise.all([prep, fetchPresigned(shopDomain, rule.resource, cache)])
            .then(function (pair) {
              var prepared = pair[0];
              var blob = prepared.blob || prepared;
              var name = prepared.name || safeFileName(entry.file.name);
              return uploadToS3(pair[1], blob, name, function (pct) {
                onStatus('Uploading ' + label + ' · ' + pct + '%');
              });
            })
            .then(function (key) {
              results = results.concat([{ keyField: rule.keyField, key: key }]);
            })
            .catch(function (err) {
              err.entry = entry;
              throw err;
            });
        });
      }, Promise.resolve())
      .then(function () {
        return results;
      });
  }

  function initMedia(form, fail) {
    var wrap = form.querySelector('[data-pr-media]');
    var input = wrap && wrap.querySelector('[data-pr-media-input]');
    var drop = wrap && wrap.querySelector('[data-pr-media-drop]');
    var list = wrap && wrap.querySelector('[data-pr-media-previews]');
    var entries = [];
    if (!wrap || !input || !list || !window.FormData || !window.File) {
      if (wrap) wrap.hidden = true;
      return {
        entries: function () {
          return [];
        },
        reset: function () {}
      };
    }

    function render() {
      list.innerHTML = '';
      entries.forEach(function (entry, i) {
        var li = document.createElement('li');
        li.className = 'tpl-pr__wr-preview';
        var media = document.createElement(entry.kind === 'video' ? 'video' : 'img');
        media.src = entry.url;
        if (entry.kind === 'video') {
          media.muted = true;
          media.playsInline = true;
          media.preload = 'metadata';
        } else {
          media.alt = '';
        }
        li.appendChild(media);
        var x = document.createElement('button');
        x.className = 'tpl-pr__wr-preview-x';
        x.type = 'button';
        x.setAttribute('aria-label', 'Remove ' + entry.file.name);
        x.textContent = '×';
        x.addEventListener('click', function () {
          remove(i);
        });
        li.appendChild(x);
        list.appendChild(li);
      });
    }

    function add(fileList) {
      var result = acceptFiles(entries, fileList);
      entries = result.entries;
      if (result.error) fail(result.error);
      render();
    }

    function remove(index) {
      var gone = entries[index];
      if (gone) URL.revokeObjectURL(gone.url);
      entries = entries.filter(function (_, i) {
        return i !== index;
      });
      render();
      input.focus();
    }

    function reset() {
      entries.forEach(function (e) {
        URL.revokeObjectURL(e.url);
      });
      entries = [];
      input.value = '';
      render();
    }

    input.addEventListener('change', function () {
      add(input.files);
      /* Clear so picking the same file again after a remove still fires. */
      input.value = '';
    });

    if (drop) {
      drop.addEventListener('dragover', function (event) {
        event.preventDefault();
        drop.classList.add('is-dragover');
      });
      drop.addEventListener('dragleave', function () {
        drop.classList.remove('is-dragover');
      });
      drop.addEventListener('drop', function (event) {
        event.preventDefault();
        drop.classList.remove('is-dragover');
        if (event.dataTransfer && event.dataTransfer.files) add(event.dataTransfer.files);
      });
    }

    return {
      entries: function () {
        return entries;
      },
      reset: reset
    };
  }

  window.tplReviewMedia = { init: initMedia, uploadAll: uploadAll };
})();
